import { animateTo, getAnimation, setDefaultAnimation, stopAnimations } from '../../common/animation-common.js';
import { classMap } from 'lit/directives/class-map.js';
import { defaultValue } from '../../common/default-value.js';
import { HasSlotController } from '../../common/slot-controller.js';
import { html } from 'lit';
import { monitor } from '../../common/monitor.js';
import { OverflowExpandController } from '../../common/overflow-expand-controller.js';
import { property, query, state } from 'lit/decorators.js';
import { scrollIntoView } from '../../common/scroll.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { waitForEvent } from '../../events/events.js';
import componentStyles from '../../styles/component.styles.js';
import formControlStyles from '../../styles/form-control.styles.js';
import styles from './select2.styles.js';
import WebModuleElement from '../../common/webmodule-element.js';
import type { CSSResultGroup, TemplateResult } from 'lit';
import type { WebmoduleRemoveEvent } from '../../events/webmodule-remove.js';
import type WebmodulePopup from '../popup/popup.js';

setDefaultAnimation('select.show', {
  keyframes: [
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 }
  ],
  options: { duration: 100, easing: 'ease' }
});

setDefaultAnimation('select.hide', {
  keyframes: [
    { opacity: 1, scale: 1 },
    { opacity: 0, scale: 0.9 }
  ],
  options: { duration: 100, easing: 'ease' }
});

/**
 * @summary Select2 - A select component that builds options from a values property instead of slotted elements.
 *
 * @slot label - The input's label. Alternatively, you can use the `label` attribute.
 * @slot prefix - Used to prepend a presentational icon or similar element to the combobox.
 * @slot clear-icon - An icon to use in lieu of the default clear icon.
 * @slot expand-icon - The icon to show when the control is expanded and collapsed. Rotates on open and close.
 *
 * @event webmodule-change - Emitted when the control's value changes.
 * @event webmodule-clear - Emitted when the control's value is cleared.
 * @event webmodule-input - Emitted when the control receives input.
 * @event webmodule-focus - Emitted when the control gains focus.
 * @event webmodule-blur - Emitted when the control loses focus.
 * @event webmodule-show - Emitted when the select's menu opens.
 * @event webmodule-after-show - Emitted after the select's menu opens and all animations are complete.
 * @event webmodule-hide - Emitted when the select's menu closes.
 * @event webmodule-after-hide - Emitted after the select's menu closes and all animations are complete.
 *
 * @csspart form-control - The form control that wraps the label, input, and help text.
 * @csspart form-control-label - The label's wrapper.
 * @csspart form-control-input - The select's wrapper.
 * @csspart combobox - The container the wraps the prefix, combobox, clear icon, and expand button.
 * @csspart prefix - The container that wraps the prefix slot.
 * @csspart display-input - The element that displays the selected option's label, an `<input>` element.
 * @csspart listbox - The listbox container where options are rendered.
 * @csspart tags - The container that houses option tags when `multiselect` is used.
 * @csspart tag - The individual tags that represent each multiselect option.
 * @csspart tag__base - The tag's base part.
 * @csspart tag__content - The tag's content part.
 * @csspart tag__remove-button - The tag's remove button.
 * @csspart tag__remove-button__base - The tag's remove button base part.
 * @csspart clear-button - The clear button.
 * @csspart expand-icon - The container that wraps the expand icon.
 *
 * @tag webmodule-select2
 */
export interface WebmoduleSelect2Value {
  text: string | TemplateResult;
  value: string;
  disabled?: boolean;
}

export default class WebmoduleSelect2 extends WebModuleElement {
  static styles: CSSResultGroup = [componentStyles, formControlStyles, styles];
  @query('.select') popup: WebmodulePopup;
  @query('.select__combobox') combobox: HTMLSlotElement;
  @query('.select__display-input') displayInput: HTMLInputElement;
  @query('.select__listbox') listbox: HTMLElement;

  @state() displayLabel = '';
  @state() private currentIndex = -1;
  @state() private selectedIndices: number[] = [];
  /** True while the combobox is expanded in place to reveal clipped text. */
  @state() expandedOverflow = false;

  private readonly overflowExpand = new OverflowExpandController(this, {
    getShell: () => this.combobox as unknown as HTMLElement,
    getTextElement: () => this.displayInput,
    canExpand: () => !this.multiple && !this.open
  });

  /** The name of the select, submitted as a name/value pair with form data. */
  @property() name = '';

  /**
   * The current value of the select. For single select, this is a string.
   * For multiple select, this is an array of strings.
   */
  @property({ type: Object })
  value: string | string[] = '';

  /** The options to display in the select. */
  @property({ type: Array })
  values: WebmoduleSelect2Value[] = [];

  /** The default value of the form control. Primarily used for resetting the form control. */
  @defaultValue() defaultValue: string | string[] = '';
  /** The select's size. */
  @property({ reflect: true }) size: 'small' | 'medium' | 'large' = 'medium';
  /** Placeholder text to show as a hint when the select is empty. */
  @property() placeholder = '';
  /** Allows more than one option to be selected. */
  @property({ type: Boolean, reflect: true }) multiple = false;
  /**
   * The maximum number of selected options to show when `multiple` is true. After the maximum, "+n" will be shown to
   * indicate the number of additional items that are selected. Set to 0 to remove the limit.
   */
  @property({ attribute: 'max-options-visible', type: Number }) maxOptionsVisible = 4;
  /** Disables the select control. */
  @property({ type: Boolean, reflect: true }) disabled = false;
  /** Adds a clear button when the select is not empty. */
  @property({ type: Boolean }) clearable = false;
  /**
   * Indicates whether the select is open. You can toggle this attribute to show and hide the menu, or you can
   * use the `show()` and `hide()` methods and this attribute will reflect the `select`'s open state.
   */
  @property({ type: Boolean, reflect: true }) open = false;
  /**
   * Enable this option to prevent the listbox from being clipped when the component is placed inside a container with
   * `overflow: auto|scroll`. Hoisting uses a fixed positioning strategy that works in many, but not all, scenarios.
   */
  @property({ type: Boolean }) hoist = false;
  /** Draws a filled select. */
  @property({ type: Boolean, reflect: true }) filled = false;
  /** Draws a pill-style select with rounded edges. */
  @property({ type: Boolean, reflect: true }) pill = false;
  /** The `select`'s label. If you need to display HTML, use the `label` slot instead. */
  @property() label = '';
  /**
   * The preferred placement of the `select`'s menu. Note that the actual placement may vary to keep the listbox
   * inside the viewport.
   */
  @property({ reflect: true }) placement: 'top' | 'bottom' = 'bottom';
  /** The `select`'s required attribute. */
  @property({ type: Boolean, reflect: true }) required = false;
  private readonly hasSlotController = new HasSlotController(this, 'label');
  private typeToSelectString = '';
  private typeToSelectTimeout: number;
  private closeWatcher: CloseWatcher | null;
  @state() private hasFocus = false;

  /** Get selected values data */
  private get selectedValues(): WebmoduleSelect2Value[] {
    return this.selectedIndices.map(i => this.values[i]).filter(Boolean);
  }

  protected get tags() {
    return this.selectedValues.map((option, index) => {
      if (index < this.maxOptionsVisible || this.maxOptionsVisible <= 0) {
        const tag = this.getTag(option, index);
        return html` <div @webmodule-remove=${(e: WebmoduleRemoveEvent) => this.handleTagRemoveByValue(e, option)}>
          ${typeof tag === 'string' ? unsafeHTML(tag) : tag}
        </div>`;
      } else if (index === this.maxOptionsVisible) {
        return html` <webmodule-tag size=${this.size}>+${this.selectedValues.length - index}</webmodule-tag>`;
      }
      return html``;
    });
  }

  /**
   * A function that customizes the tags to be rendered when multiple=true.
   */
  @property() getTag: (option: WebmoduleSelect2Value, index: number) => TemplateResult | string | HTMLElement =
    option => {
      const text = typeof option.text === 'string' ? option.text : '';
      return html`
        <webmodule-tag
          part="tag"
          exportparts="
              base:tag__base,
              content:tag__content,
              remove-button:tag__remove-button,
              remove-button__base:tag__remove-button__base
            "
          ?pill=${this.pill}
          size=${this.size}
          removable
          @webmodule-remove=${(event: WebmoduleRemoveEvent) => this.handleTagRemoveByValue(event, option)}
        >
          ${text}
        </webmodule-tag>
      `;
    };

  connectedCallback() {
    super.connectedCallback();
    this.open = false;
    this.syncSelectionFromValue();
  }

  @monitor('disabled', { delayMonitorUntilFirstUpdate: true })
  handleDisabledChange() {
    if (this.disabled) {
      this.open = false;
      this.handleOpenChange();
    }
  }

  @monitor('value', { delayMonitorUntilFirstUpdate: true })
  handleValueChange() {
    this.syncSelectionFromValue();
  }

  @monitor('values', { delayMonitorUntilFirstUpdate: true })
  handleValuesChange() {
    this.syncSelectionFromValue();
  }

  private syncSelectionFromValue() {
    const valueArray = Array.isArray(this.value) ? this.value : [this.value];
    const newSelectedIndices: number[] = [];

    for (let i = 0; i < this.values.length; i++) {
      if (valueArray.includes(this.values[i].value)) {
        newSelectedIndices.push(i);
      }
    }

    this.selectedIndices = newSelectedIndices;
    this.updateDisplayLabel();
  }

  private updateDisplayLabel() {
    const numOptionsSelected = (num: number) => {
      if (num === 0) return 'No options selected';
      if (num === 1) return '1 option selected';
      return `${num} options selected`;
    };

    if (this.multiple) {
      if (this.placeholder && this.selectedIndices.length === 0) {
        this.displayLabel = '';
      } else {
        this.displayLabel = numOptionsSelected(this.selectedIndices.length);
      }
    } else {
      const selected = this.selectedValues[0];
      if (selected) {
        this.displayLabel = typeof selected.text === 'string' ? selected.text : '';
      } else {
        this.displayLabel = '';
      }
    }
  }

  @monitor('open', { delayMonitorUntilFirstUpdate: true })
  async handleOpenChange() {
    if (this.open) {
      this.overflowExpand.collapse();
    }
    if (this.open && !this.disabled) {
      // Reset the current option to first selected or first available
      this.currentIndex = this.selectedIndices[0] ?? 0;

      this.emit('webmodule-show');
      this.addOpenListeners();

      await stopAnimations(this);
      this.listbox.hidden = false;
      this.popup.active = true;

      requestAnimationFrame(() => {
        this.scrollCurrentIntoView();
      });

      const { keyframes, options } = getAnimation(this, 'select.show');
      await animateTo(this.popup.popup, keyframes, options);

      this.scrollCurrentIntoView();
      this.emit('webmodule-after-show');
    } else {
      this.emit('webmodule-hide');
      this.removeOpenListeners();

      await stopAnimations(this);
      const { keyframes, options } = getAnimation(this, 'select.hide');
      await animateTo(this.popup.popup, keyframes, options);
      this.listbox.hidden = true;
      this.popup.active = false;

      this.emit('webmodule-after-hide');
    }
  }

  private scrollCurrentIntoView() {
    const currentOption = this.listbox?.querySelector('webmodule-option[current]');
    if (currentOption) {
      scrollIntoView(currentOption as HTMLElement, this.listbox, 'vertical', 'auto');
    }
  }

  /** Shows the option list. */
  show() {
    if (this.open || this.disabled) {
      this.open = false;
      return undefined;
    }

    this.open = true;
    return waitForEvent(this, 'webmodule-after-show');
  }

  /** Hides the option list. */
  hide() {
    if (!this.open || this.disabled) {
      this.open = false;
      return undefined;
    }

    this.open = false;
    return waitForEvent(this, 'webmodule-after-hide');
  }

  focus(options?: FocusOptions) {
    this.displayInput.focus(options);
  }

  /** Removes focus from the control. */
  blur() {
    this.displayInput.blur();
  }

  render() {
    const hasLabelSlot = this.hasSlotController.checkFor('label');
    const hasLabel = this.label ? true : !!hasLabelSlot;
    const hasClearIcon = this.clearable && !this.disabled && this.value?.length > 0;

    const isPlaceholderVisible = this.placeholder && this.value?.length === 0;

    return html`
      <div
        part="form-control"
        class=${classMap({
          'form-control': true,
          'form-control--small': this.size === 'small',
          'form-control--medium': this.size === 'medium',
          'form-control--large': this.size === 'large',
          'form-control--has-label': hasLabel
        })}
      >
        <label
          id="label"
          part="form-control-label"
          class="form-control__label"
          aria-hidden=${hasLabel ? 'false' : 'true'}
          @click=${this.handleLabelClick}
        >
          <slot name="label">${this.label}</slot>
        </label>

        <div part="form-control-input" class="form-control-input">
          <webmodule-popup
            class=${classMap({
              select: true,
              'select--standard': true,
              'select--filled': this.filled,
              'select--pill': this.pill,
              'select--open': this.open,
              'select--disabled': this.disabled,
              'select--multiple': this.multiple,
              'select--focused': this.hasFocus,
              'select--placeholder-visible': isPlaceholderVisible,
              'select--top': this.placement === 'top',
              'select--bottom': this.placement === 'bottom',
              'select--small': this.size === 'small',
              'select--medium': this.size === 'medium',
              'select--large': this.size === 'large',
              'select--expanded-overflow': this.expandedOverflow
            })}
            placement=${this.placement}
            strategy=${this.hoist ? 'fixed' : 'absolute'}
            flip
            shift
            sync="width"
            auto-size="vertical"
            auto-size-padding="10"
          >
            <div
              part="combobox"
              class="select__combobox"
              slot="anchor"
              @keydown=${this.handleComboboxKeyDown}
              @mousedown=${this.handleComboboxMouseDown}
            >
              <slot part="prefix" name="prefix" class="select__prefix"></slot>

              <input
                part="display-input"
                class="select__display-input"
                type="text"
                placeholder=${this.placeholder}
                .disabled=${this.disabled}
                .value=${this.displayLabel}
                autocomplete="off"
                spellcheck="false"
                autocapitalize="off"
                readonly
                aria-controls="listbox"
                aria-expanded=${this.open ? 'true' : 'false'}
                aria-haspopup="listbox"
                aria-labelledby="label"
                aria-disabled=${this.disabled ? 'true' : 'false'}
                role="combobox"
                tabindex="0"
                @focus=${this.handleFocus}
                @blur=${this.handleBlur}
              />

              ${this.multiple ? html` <div part="tags" class="select__tags">${this.tags}</div>` : ''}
              ${hasClearIcon
                ? html`
                    <button
                      part="clear-button"
                      class="select__clear"
                      type="button"
                      aria-label="Clear control"
                      @mousedown=${this.handleClearMouseDown}
                      @click=${this.handleClearClick}
                      tabindex="-1"
                    >
                      <slot name="clear-icon">
                        <webmodule-icon name="x-circle-fill"></webmodule-icon>
                      </slot>
                    </button>
                  `
                : ''}

              <slot name="expand-icon" part="expand-icon" class="select__expand-icon">
                <webmodule-icon name="chevron-down"></webmodule-icon>
              </slot>
            </div>

            <div
              id="listbox"
              role="listbox"
              aria-expanded=${this.open ? 'true' : 'false'}
              aria-multiselectable=${this.multiple ? 'true' : 'false'}
              aria-labelledby="label"
              part="listbox"
              class="select__listbox"
              tabindex="-1"
              @mouseup=${this.handleOptionClick}
            >
              ${this.renderItems()}
            </div>
          </webmodule-popup>
        </div>
      </div>
    `;
  }

  private renderItems() {
    return this.values.map((option, index) => {
      const isSelected = this.selectedIndices.includes(index);
      const isCurrent = this.currentIndex === index;
      const isDisabled = option.disabled ?? false;

      return html`
        <webmodule-option
          value=${option.value}
          ?selected=${isSelected}
          ?current=${isCurrent}
          ?disabled=${isDisabled}
          data-index=${index}
        >
          ${option.text}
        </webmodule-option>
      `;
    });
  }

  private addOpenListeners() {
    //
    // Listen on the root node instead of the document in case the elements are inside a shadow root
    //
    document.addEventListener('focusin', this.handleDocumentFocusIn);
    document.addEventListener('keydown', this.handleDocumentKeyDown);
    document.addEventListener('mousedown', this.handleDocumentMouseDown);

    // If the component is rendered in a shadow root, we need to attach the focusin listener there too
    if (this.getRootNode() !== document) {
      this.getRootNode().addEventListener('focusin', this.handleDocumentFocusIn);
    }

    if ('CloseWatcher' in window) {
      this.closeWatcher?.destroy();
      this.closeWatcher = new CloseWatcher();
      this.closeWatcher.onclose = () => {
        if (this.open) {
          this.hide();
          this.displayInput.focus({ preventScroll: true });
        }
      };
    }
  }

  private removeOpenListeners() {
    document.removeEventListener('focusin', this.handleDocumentFocusIn);
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);

    if (this.getRootNode() !== document) {
      this.getRootNode().removeEventListener('focusin', this.handleDocumentFocusIn);
    }

    this.closeWatcher?.destroy();
  }

  private handleFocus() {
    this.hasFocus = true;
    this.displayInput.setSelectionRange(0, 0);
    this.emit('webmodule-focus');
  }

  private handleBlur() {
    this.hasFocus = false;
    this.emit('webmodule-blur');
  }

  private handleDocumentFocusIn = (event: KeyboardEvent) => {
    // Close when focusing out of the select
    const path = event.composedPath();
    if (this && !path.includes(this)) {
      this.hide();
    }
  };

  private handleDocumentKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    const isClearButton = target.closest('.select__clear') !== null;
    const isIconButton = target.closest('webmodule-icon-button') !== null;

    if (isClearButton || isIconButton) {
      return;
    }

    // Close when pressing escape
    if (event.key === 'Escape' && this.open && !this.closeWatcher) {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
      this.displayInput.focus({ preventScroll: true });
    }

    // Handle enter and space. When pressing space, we allow for type to select behaviors so if there's anything in the
    // buffer we _don't_ close it.
    if (event.key === 'Enter' || (event.key === ' ' && this.typeToSelectString === '')) {
      event.preventDefault();
      event.stopImmediatePropagation();

      // If it's not open, open it
      if (!this.open) {
        this.show();
        return;
      }

      // If it is open, update the value based on the current selection and close it
      const currentOption = this.values[this.currentIndex];
      if (currentOption && !currentOption.disabled) {
        this.selectOptionAtIndex(this.currentIndex);

        // Emit after updating
        this.updateComplete.then(() => {
          this.emit('webmodule-input');
          this.emit('webmodule-change');
        });

        if (!this.multiple) {
          this.hide();
          this.displayInput.focus({ preventScroll: true });
        }
      }

      return;
    }

    // Navigate options
    if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      let newIndex = Math.max(0, this.currentIndex);

      // Prevent scrolling
      event.preventDefault();

      // Open it
      if (!this.open) {
        this.show();

        if (this.currentIndex >= 0) {
          return;
        }
      }

      if (event.key === 'ArrowDown') {
        newIndex = this.currentIndex + 1;
        if (newIndex > this.values.length - 1) newIndex = 0;
      } else if (event.key === 'ArrowUp') {
        newIndex = this.currentIndex - 1;
        if (newIndex < 0) newIndex = this.values.length - 1;
      } else if (event.key === 'Home') {
        newIndex = 0;
      } else if (event.key === 'End') {
        newIndex = this.values.length - 1;
      }

      this.currentIndex = newIndex;
      this.requestUpdate();
      this.updateComplete.then(() => this.scrollCurrentIntoView());
    }

    // All other "printable" keys trigger type to select
    if ((event.key && event.key.length === 1) || event.key === 'Backspace') {
      // Don't block important key combos like CMD+R
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      // Open, unless the key that triggered is backspace
      if (!this.open) {
        if (event.key === 'Backspace') {
          return;
        }

        this.show();
      }

      event.stopPropagation();
      event.preventDefault();

      clearTimeout(this.typeToSelectTimeout);
      this.typeToSelectTimeout = window.setTimeout(() => (this.typeToSelectString = ''), 1000);

      if (event.key === 'Backspace') {
        this.typeToSelectString = this.typeToSelectString.slice(0, -1);
      } else {
        this.typeToSelectString += event.key.toLowerCase();
      }

      for (let i = 0; i < this.values.length; i++) {
        const option = this.values[i];
        const label = typeof option.text === 'string' ? option.text.toLowerCase() : '';

        if (label.startsWith(this.typeToSelectString)) {
          this.currentIndex = i;
          this.requestUpdate();
          this.updateComplete.then(() => this.scrollCurrentIntoView());
          break;
        }
      }
    }
  };

  private handleDocumentMouseDown = (event: MouseEvent) => {
    // Close when clicking outside of the select
    const path = event.composedPath();
    if (this && !path.includes(this)) {
      this.hide();
    }
  };

  private handleLabelClick() {
    this.displayInput.focus();
  }

  private handleComboboxMouseDown(event: MouseEvent) {
    const path = event.composedPath();
    const isIconButton = path.some(el => el instanceof Element && el.tagName.toLowerCase() === 'webmodule-icon-button');

    // Ignore disabled controls and clicks on tags (remove buttons)
    if (this.disabled || isIconButton) {
      return;
    }

    event.preventDefault();
    this.displayInput.focus({ preventScroll: true });
    this.open = !this.open;
  }

  private handleComboboxKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      return;
    }

    event.stopPropagation();
    this.handleDocumentKeyDown(event);
  }

  private handleClearClick(event: MouseEvent) {
    event.stopPropagation();

    if (this.value !== '') {
      this.selectedIndices = [];
      if (this.multiple) {
        this.value = [];
      } else {
        this.value = '';
      }
      this.updateDisplayLabel();
      this.displayInput.focus({ preventScroll: true });

      // Emit after update
      this.updateComplete.then(() => {
        this.emit('webmodule-clear');
        this.emit('webmodule-input');
        this.emit('webmodule-change');
      });
    }
  }

  private handleClearMouseDown(event: MouseEvent) {
    // Don't lose focus or propagate events when clicking the clear button
    event.stopPropagation();
    event.preventDefault();
  }

  private handleOptionClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const optionEl = target.closest('webmodule-option') as HTMLElement;

    if (!optionEl) return;

    const index = parseInt(optionEl.dataset.index ?? '-1', 10);
    if (index < 0 || index >= this.values.length) return;

    const option = this.values[index];
    if (option.disabled) return;

    const oldValue = this.value;

    this.selectOptionAtIndex(index);

    // Set focus after updating so the value is announced by screen readers
    this.updateComplete.then(() => this.displayInput.focus({ preventScroll: true }));

    if (this.value !== oldValue) {
      // Emit after updating
      this.updateComplete.then(() => {
        this.emit('webmodule-input');
        this.emit('webmodule-change');
      });
    }

    if (!this.multiple) {
      this.hide();
      this.displayInput.focus({ preventScroll: true });
    }
  }

  private selectOptionAtIndex(index: number) {
    if (this.multiple) {
      const idx = this.selectedIndices.indexOf(index);
      if (idx >= 0) {
        this.selectedIndices = this.selectedIndices.filter(i => i !== index);
      } else {
        this.selectedIndices = [...this.selectedIndices, index];
      }
      this.value = this.selectedIndices.map(i => this.values[i].value);
    } else {
      this.selectedIndices = [index];
      this.value = this.values[index].value;
    }
    this.updateDisplayLabel();
  }

  private handleTagRemoveByValue(event: WebmoduleRemoveEvent, option: WebmoduleSelect2Value) {
    event.stopPropagation();

    if (!this.disabled) {
      const index = this.values.findIndex(v => v.value === option.value);
      if (index >= 0) {
        this.selectedIndices = this.selectedIndices.filter(i => i !== index);
        this.value = this.selectedIndices.map(i => this.values[i].value);
        this.updateDisplayLabel();

        // Emit after updating
        this.updateComplete.then(() => {
          this.emit('webmodule-input');
          this.emit('webmodule-change');
        });
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'webmodule-select': WebmoduleSelect2;
  }
}
